import { useFlags } from '@atlaskit/flag';
import { AddEditProcessTypeStatusFormFields } from 'core/config/form-fields';
import { useDialog } from 'core/providers/DialogProvider';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import { useState } from 'react';
import { IButton } from 'shared/components/buttons/button-interface';
import { ProcessTypeStatusesApiService } from 'core/api';
import { useAuthState } from 'core/providers/AuthProvider';
import SharedForm from 'shared/components/form/form';
import { ISharedFormField } from 'shared/components/form/form-interface';
import SharedDialogBase from 'shared/components/dialog-base/dialog-base';
import { IProcessTypeStatusDao, IProcessTypeStatusDto } from 'core/api/types/process-type-statuses.interface';
import { IAddEditProcessTypeStatusFormOutput } from '../types';

const AddEditProcessTypeStatusDialog = ({ processTypeStatus }: { processTypeStatus?: IProcessTypeStatusDao }) => {
  const { userData } = useAuthState();
  const flags = useFlags();
  const dialog = useDialog();

  const [formSubmitting, setFormSubmitting] = useState(false);

  const addEditProcessTypeStatus = async (data: IAddEditProcessTypeStatusFormOutput) => {
    setFormSubmitting(true);

    const payload: IProcessTypeStatusDto = {
      active: data.active,
      name: data.name,
    };

    try {
      if (processTypeStatus) {
        await ProcessTypeStatusesApiService.update(processTypeStatus.uid, payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      } else {
        await ProcessTypeStatusesApiService.set(payload, {
          fullName: userData?.fullName ?? '',
          uid: userData?.uid ?? '',
        });
      }
      showSuccessFlag(
        processTypeStatus ? 'Process type status updated' : 'Process type status created',
        `The process type status has been ${processTypeStatus ? 'updated' : 'created'} successfully`,
        flags
      );
      dialog?.closeDialog();
    } catch (error) {
      showErrorFlag(
        processTypeStatus ? 'Process type status update failed' : 'Process type status creation failed',
        `Unable to ${processTypeStatus ? 'update' : 'create'} a process type status, please try again.`,
        flags
      );
      setFormSubmitting(false);
    }
  };

  const cancelButton: IButton = {
    onClick: () => dialog?.closeDialog(),
    label: 'Cancel',
    appearance: 'subtle',
    type: 'button',
  };

  const getDefaultValue = (processTypeStatus: IProcessTypeStatusDao, key: string) => {
    switch (key) {
      default:
        return processTypeStatus[key as keyof IProcessTypeStatusDao];
    }
  };

  const fields = AddEditProcessTypeStatusFormFields.map((field) => ({
    ...field,
    defaultValue: processTypeStatus ? getDefaultValue(processTypeStatus, field.key) : field.defaultValue,
  }));

  const customContent = () => {
    return (
      <SharedForm
        className='p-4 overflow-y-auto'
        onSubmit={addEditProcessTypeStatus}
        fields={fields as ISharedFormField[]}
        buttonLabel='Submit'
        loading={formSubmitting}
        cancelButton={cancelButton}
      />
    );
  };

  return (
    <SharedDialogBase
      title={processTypeStatus ? 'Update process type status' : 'Add process type status'}
      customContentTemplate={customContent()}
      showButtons={false}
    />
  );
};

export default AddEditProcessTypeStatusDialog;
